import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, AlertTriangle, XCircle, Play } from 'lucide-react';

import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';

interface OrdenesStatusCardProps {
  ordenes: OrdenTrabajo[];
}

export const OrdenesStatusCard: React.FC<OrdenesStatusCardProps> = ({ ordenes }) => {
  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'creada':
        return <Clock className="w-4 h-4" />;
      case 'asignada':
        return <Play className="w-4 h-4" />;
      case 'en_progreso':
        return <AlertTriangle className="w-4 h-4" />;
      case 'finalizada':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelada':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getEstadoBadgeVariant = (estado: string) => {
    switch (estado) {
      case 'creada':
        return 'secondary';
      case 'asignada':
        return 'outline';
      case 'en_progreso':
        return 'default';
      case 'finalizada':
        return 'outline';
      case 'cancelada':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case 'baja':
        return 'text-blue-600';
      case 'media':
        return 'text-yellow-600';
      case 'alta':
        return 'text-orange-600';
      case 'urgente':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getEstadoText = (estado: string) => {
    switch (estado) {
      case 'creada':
        return 'Creada';
      case 'asignada':
        return 'Asignada';
      case 'en_progreso':
        return 'En Progreso';
      case 'finalizada':
        return 'Finalizada';
      case 'cancelada':
        return 'Cancelada';
      default:
        return estado;
    }
  };

  // Group orders by status for better visualization
  const ordenesPorEstado = ordenes.reduce((acc, orden) => {
    if (!acc[orden.estado]) acc[orden.estado] = [];
    acc[orden.estado].push(orden);
    return acc;
  }, {} as Record<string, OrdenTrabajo[]>);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Estado de Órdenes de Trabajo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(ordenesPorEstado).map(([estado, ordenesDelEstado]) => (
          <div key={estado} className="space-y-2">
            <div className="flex items-center gap-2">
              {getEstadoIcon(estado)}
              <h4 className="font-medium">
                {getEstadoText(estado)} ({ordenesDelEstado.length})
              </h4>
            </div>
            <div className="space-y-2 ml-6">
              {ordenesDelEstado.slice(0, 3).map((orden) => (
                <div key={orden.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{orden.numero_ot}</span>
                      <Badge variant={getEstadoBadgeVariant(orden.estado)}>
                        {getEstadoText(orden.estado)}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{orden.titulo}</p>
                    <p className="text-xs text-muted-foreground">{orden.cliente?.nombre}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-medium ${getPrioridadColor(orden.prioridad)}`}>
                      {orden.prioridad}
                    </span>
                  </div>
                </div>
              ))}
              {ordenesDelEstado.length > 3 && (
                <p className="text-xs text-muted-foreground ml-2">
                  y {ordenesDelEstado.length - 3} más...
                </p>
              )}
            </div>
          </div>
        ))}
        
        {ordenes.length === 0 && (
          <p className="text-center text-muted-foreground py-4">
            No hay órdenes de trabajo disponibles
          </p>
        )}
      </CardContent>
    </Card>
  );
};